import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useNavigation } from '@client/contexts/NavigationContext';
import { useVisibleSectionIndices } from '@client/hooks/useFormConditionals';
import { FormParameterValues, interpolateText } from '@client/hooks/useFormParameters';
import { selectClient } from '@client/model';
import { ArrowLeft, Check, ChevronDown, ChevronRight, CircleAlert } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface DesktopSidebarProps {
  clientPolicyApplicationId: string;
  formTitle: string;
  formDefinition: PolicyFormDefinition;
  product: string;
  currentSectionIndex: number;
  currentSubsectionIndex: number;
  completedSubsections: Set<string>;
  onSectionChange: (sectionIndex: number, subsectionIndex: number) => void;
  visibleSections: PolicyFormSection[];
  parameters?: FormParameterValues;
}

const PolicyApplicationDesktopSidebar: React.FC<DesktopSidebarProps> = ({
  clientPolicyApplicationId,
  formTitle,
  formDefinition,
  product,
  currentSectionIndex,
  currentSubsectionIndex,
  completedSubsections,
  onSectionChange,
  visibleSections,
  parameters = {},
}) => {
  const { navigationService, state } = useNavigation();
  const navigate = useNavigate();
  const { clientId } = useSelector(selectClient);
  // Track the last user-toggled section (only one at a time)
  const [lastToggledSection, setLastToggledSection] = React.useState<number | null>(null);

  // Get the current section and subsection from absolute indices
  const currentSection = formDefinition.sections[currentSectionIndex];
  const currentSubsection = currentSection?.subsections[currentSubsectionIndex];

  // Convert absolute indices to visible indices using the new hook
  const visibleSectionsData = visibleSections.map(section => ({
    section,
    subsections: section.subsections,
  }));

  const { visibleSectionIndex, visibleSubsectionIndex } = useVisibleSectionIndices(
    currentSection,
    currentSubsection,
    visibleSectionsData,
  );

  const expandedSections = React.useMemo(() => {
    const expanded = new Set<number>();

    // Always expand the current visible section
    if (visibleSectionIndex >= 0) {
      expanded.add(visibleSectionIndex);
    }

    // Only expand the last toggled section if it's different from current
    if (lastToggledSection !== null && lastToggledSection !== visibleSectionIndex) {
      expanded.add(lastToggledSection);
    }

    return expanded;
  }, [visibleSectionIndex, lastToggledSection]);

  const toggleSection = (sectionIndex: number) => {
    // Don't allow toggling the current section
    if (sectionIndex === visibleSectionIndex) {
      return;
    }

    // If clicking on the already toggled section, close it
    if (lastToggledSection === sectionIndex) {
      setLastToggledSection(null);
    } else {
      // Otherwise, set this as the new toggled section (closes previous one)
      setLastToggledSection(sectionIndex);
    }
  };

  const isSectionComplete = (sectionIndex: number) => {
    const section = visibleSections[sectionIndex];
    return section.subsections.every(sub =>
      navigationService.isSubsectionComplete(
        sub.id,
        state.formData,
        state.visitedSubsections,
        state.currentPosition?.subsectionId,
      ),
    );
  };

  return (
    <div className="sticky top-0 hidden h-screen w-80 overflow-y-auto border-r bg-white shadow-sm md:block">
      <div className="p-6">
        <div className="mb-6 flex items-center gap-4 p-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              navigate(`/clients/${clientId}/policy-application/${clientPolicyApplicationId}`)
            }
            className="h-8 w-8 bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-gray-900">
            {formTitle} {product}
          </h2>
        </div>
        <div className="space-y-2">
          {visibleSections.map((section, sectionIndex) => (
            <Collapsible
              key={section.id}
              open={expandedSections.has(sectionIndex)}
              onOpenChange={() => toggleSection(sectionIndex)}
            >
              <CollapsibleTrigger asChild>
                <div
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors',
                    sectionIndex === visibleSectionIndex
                      ? 'border-primary-200 bg-primary-50 border'
                      : 'hover:bg-gray-50',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium',
                      isSectionComplete(sectionIndex)
                        ? 'bg-primary text-white'
                        : sectionIndex === visibleSectionIndex
                          ? 'border-primary text-primary border bg-white'
                          : 'bg-primary-200 text-gray-700',
                    )}
                  >
                    {isSectionComplete(sectionIndex) ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      sectionIndex + 1
                    )}
                  </div>
                  <span
                    className={cn(
                      'flex flex-1 items-center text-left text-sm font-medium',
                      sectionIndex === visibleSectionIndex ? 'text-primary-dark' : 'text-gray-700',
                    )}
                  >
                    {interpolateText(section.title, parameters)}
                    {navigationService.isSectionWarning(section) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <CircleAlert className="ml-1 h-4 w-4 flex-shrink-0 cursor-pointer text-yellow-700" />
                          </TooltipTrigger>

                          <TooltipContent side="right">
                            <p>Some required items in this section still need attention</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                  {expandedSections.has(sectionIndex) ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
                  )}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="ml-9 mt-2 space-y-1">
                {section.subsections.map((subsection, subsectionIndex) => (
                  <div
                    key={subsection.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm transition-colors',
                      sectionIndex === visibleSectionIndex &&
                        subsectionIndex === visibleSubsectionIndex
                        ? 'bg-primary-100 text-primary-dark'
                        : 'text-gray-700 hover:bg-gray-50',
                    )}
                    onClick={() => onSectionChange(sectionIndex, subsectionIndex)}
                  >
                    <div
                      className={cn(
                        'h-2 w-2 flex-shrink-0 rounded-full',
                        navigationService.isSubsectionWarning(subsection.id)
                          ? 'bg-yellow-700'
                          : completedSubsections.has(subsection.id)
                            ? 'bg-primary'
                            : sectionIndex === visibleSectionIndex &&
                                subsectionIndex === visibleSubsectionIndex
                              ? 'bg-primary'
                              : 'bg-primary-200',
                      )}
                    />
                    <span className="truncate">
                      {interpolateText(subsection.title, parameters)}
                    </span>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PolicyApplicationDesktopSidebar;
